import { useTranslation } from 'react-i18next';
/**
 * `/site` — site administration.
 *
 * Screen 23. Three surfaces behind one page: the review queue, the audit, and the standing reminder
 * of what this authority is not.
 *
 * > Site Administration governs the platform. Ownership governs Resources. These stay separate.
 *
 * That is why the page shows a review queue and an audit log and **nothing resembling a list of
 * everyone's liturgies**. There is no route behind this screen that reads private planning content,
 * which is the point rather than an omission (L2-09 §3, 04-authorization.md).
 */

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';

import type { ApiError } from '../../api/client';
import { Notice } from '../../components/primitives';
import { useAuth } from '../auth/useAuth';
import {
  approveRequest,
  assignSiteOrganizationAdmin,
  createOrganizationDirectly,
  designateTemplate,
  DESIGNATION_COPY,
  disableAccount,
  enableAccount,
  fetchAudit,
  fetchOrganizations,
  fetchReviewQueue,
  fetchSiteAccounts,
  fetchSiteOverview,
  ORGANIZATION_KIND_LABELS,
  type OrganizationKind,
  rejectRequest,
  retireOrganization,
  withdrawDesignation,
  type DesignationKind,
  type OrganizationRequest,
} from './api';
import { fetchTemplates } from '../templates/api';
import './organizations.css';

type Tab = 'requests' | 'organizations' | 'accounts' | 'templates' | 'audit' | 'integrations';

import { SiteIntegrationsScreen } from './SiteIntegrationsScreen';

export function SiteAdminScreen({
  defaultTab = 'requests',
}: {
  defaultTab?: Tab;
}): React.JSX.Element {
  const { t } = useTranslation('common');
  const tab = defaultTab;

  const overview = useQuery({ queryKey: ['site-overview'], queryFn: fetchSiteOverview });

  const code = (overview.error as ApiError | undefined)?.code;

  // A different refusal entirely, and the only one the reader can act on: they hold the authority
  // and this browser has not proved it is them. Checked before the denial branch, because it is
  // reached only by somebody who genuinely is a Site Administrator.
  if (code === 'mfa_required') {
    return (
      <div className="org">
        <header className="org__head">
          <h1 className="org__title">{t('site_admin.title')}</h1>
        </header>
        <section className="org__card org__card--warm">
          <p className="org__eyebrow">{t('site_admin.mfa_title')}</p>
          <p className="org__body">{t('site_admin.mfa_body')}</p>
          <p className="org__fine">{t('site_admin.mfa_fine')}</p>
          <p style={{ marginTop: 14 }}>
            <Link className="org__link" to="/security">
              {t('site_admin.mfa_cta')}
            </Link>
          </p>
        </section>
      </div>
    );
  }

  // Both codes, because the kernel conceals a non-public resource with 404 rather than 403 — so
  // "not a site administrator" arrives as `resource_not_found`. Either way this is the ordinary
  // state for almost everyone, not an error worth a red banner.
  const denied = code === 'authorization_denied' || code === 'resource_not_found';

  if (denied) {
    return (
      <div className="org">
        <header className="org__head">
          <h1 className="org__title">{t('site_admin.title')}</h1>
        </header>
        <section className="org__card">
          <p className="org__body">{t('site_admin.denied_body')}</p>
          <Link className="org__link" to="/liturgies">
            {t('site_admin.back_liturgies')}
          </Link>
        </section>
      </div>
    );
  }

  return (
    <div className="org">
      <header className="org__head">
        <div>
          <h1 className="org__title">{t('site_admin.title')}</h1>
          <p className="org__lede">{t('site_admin.lede')}</p>
        </div>
        <Link className="org__link" to="/organizations">
          {t('organizations.title')} →
        </Link>
      </header>

      <nav className="org__tabs">
        <Link
          to="/site/organization-requests"
          className={`org__tab${tab === 'requests' ? ' org__tab--on' : ''}`}
        >
          {t('site_admin.tab_requests')}
          {overview.data?.pending_organization_requests ? (
            <span className="org__badge">{overview.data.pending_organization_requests}</span>
          ) : null}
        </Link>
        <Link
          to="/site/organizations"
          className={`org__tab${tab === 'organizations' ? ' org__tab--on' : ''}`}
        >
          {t('organizations.title')}
        </Link>
        <Link
          to="/site/accounts"
          className={`org__tab${tab === 'accounts' ? ' org__tab--on' : ''}`}
        >
          {t('site_admin.tab_accounts')}
        </Link>
        <Link
          to="/site/templates"
          className={`org__tab${tab === 'templates' ? ' org__tab--on' : ''}`}
        >
          {t('nav.templates')}
        </Link>
        <Link to="/site/audit" className={`org__tab${tab === 'audit' ? ' org__tab--on' : ''}`}>
          {t('site_admin.audit', 'Administrative audit')}
        </Link>
        <Link
          to="/site/integrations"
          className={`org__tab${tab === 'integrations' ? ' org__tab--on' : ''}`}
        >
          {t('site_admin.tab_integrations')}
        </Link>
      </nav>

      <div className="org__grid">
        <div className="org__main">
          {tab === 'requests' ? (
            <ReviewQueue />
          ) : tab === 'organizations' ? (
            <OrganizationsManager />
          ) : tab === 'accounts' ? (
            <AccountsManager />
          ) : tab === 'templates' ? (
            <TemplateCuration />
          ) : tab === 'audit' ? (
            <AuditLog />
          ) : (
            <SiteIntegrationsScreen />
          )}
        </div>

        <aside className="org__side">
          <section className="org__card org__card--warm">
            <p className="org__eyebrow">{t('site_admin.authority_not_title')}</p>
            <p className="org__body">{t('site_admin.authority_not_body')}</p>
            <p className="org__fine">{t('site_admin.authority_not_fine')}</p>
          </section>

          <section className="org__card">
            <p className="org__eyebrow">{t('site_admin.recorded_title')}</p>
            <p className="org__body">{t('site_admin.recorded_body')}</p>
          </section>
        </aside>
      </div>
    </div>
  );
}

function ReviewQueue(): React.JSX.Element {
  const queryClient = useQueryClient();
  const [showAll, setShowAll] = useState(false);

  const queue = useQuery({
    queryKey: ['site-requests', showAll],
    queryFn: () => fetchReviewQueue(showAll),
  });

  const refresh = async (): Promise<void> => {
    await queryClient.invalidateQueries({ queryKey: ['site-requests'] });
    await queryClient.invalidateQueries({ queryKey: ['site-overview'] });
  };

  return (
    <section className="org__card">
      <div className="org__card-head">
        <p className="org__eyebrow">{showAll ? 'Every request' : 'Awaiting review'}</p>
        <button type="button" className="org__ghost" onClick={() => setShowAll(!showAll)}>
          {showAll ? 'Only pending' : 'Show decided'}
        </button>
      </div>

      {queue.isPending ? (
        <p className="org__fine">Loading…</p>
      ) : queue.data?.data.length === 0 ? (
        <p className="org__fine">Nothing is waiting.</p>
      ) : (
        <ul className="org__list">
          {(queue.data?.data ?? []).map((row) => (
            <ReviewRow key={row.id} row={row} onDecided={refresh} />
          ))}
        </ul>
      )}
    </section>
  );
}

function ReviewRow({
  row,
  onDecided,
}: {
  row: OrganizationRequest;
  onDecided: () => Promise<void>;
}): React.JSX.Element {
  const [name, setName] = useState(row.requested_name);
  const [note, setNote] = useState('');
  const [open, setOpen] = useState(false);

  const approve = useMutation({
    mutationFn: () => approveRequest(row.id, { name, kind: row.requested_kind }),
    onSuccess: onDecided,
  });

  const reject = useMutation({
    mutationFn: () => rejectRequest(row.id, note),
    onSuccess: onDecided,
  });

  const error = [approve.error, reject.error].find((e) => e instanceof Error) as
    ApiError | undefined;

  if (row.status !== 'pending') {
    return (
      <li className="org__request">
        <div>
          <div className="org__row-name">{row.requested_name}</div>
          <div className="org__row-meta">
            {row.requester} · {row.created_at.slice(0, 10)}
            {row.decision_note ? ` · ${row.decision_note}` : ''}
          </div>
        </div>
        <span className={`org-pill org-pill--${row.status}`}>{row.status}</span>
        <span />
      </li>
    );
  }

  return (
    <li className="org__review">
      <div className="org__review-head">
        <div>
          <div className="org__row-name">{row.requested_name}</div>
          <div className="org__row-meta">
            {ORGANIZATION_KIND_LABELS[
              row.requested_kind as keyof typeof ORGANIZATION_KIND_LABELS
            ] ?? row.requested_kind}{' '}
            · requested by {row.requester} · {row.created_at.slice(0, 10)}
          </div>
          {row.detail ? <div className="org__row-detail">{row.detail}</div> : null}
        </div>
        <button type="button" className="org__ghost" onClick={() => setOpen(!open)}>
          {open ? 'Close' : 'Review'}
        </button>
      </div>

      {/* The first thing a reviewer needs, before they read anything else. */}
      {row.possible_duplicate ? (
        <p className="org__duplicate">
          This may already exist as <strong>{row.possible_duplicate.name}</strong>. If so, reject it
          and say which one to use.
        </p>
      ) : null}

      {open ? (
        <div className="org__review-body">
          <label className="org__field">
            <span className="org__field-label">Canonical name</span>
            <input value={name} onChange={(e) => setName(e.target.value)} />
            <span className="org__fine">
              Correct the spelling here. This is the name everyone will see and search against.
            </span>
          </label>

          <label className="org__field">
            <span className="org__field-label">Reason, if rejecting</span>
            <input
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Already registered as…"
            />
            <span className="org__fine">
              Required to reject. Without it the same request simply comes back.
            </span>
          </label>

          {error ? <Notice referenceId={error.correlationId}>{error.message}</Notice> : null}

          <div className="org__actions">
            <button
              type="button"
              className="org__btn"
              disabled={approve.isPending}
              onClick={() => approve.mutate()}
            >
              Approve
            </button>
            <button
              type="button"
              className="org__btn org__btn--quiet"
              disabled={reject.isPending || note.trim() === ''}
              onClick={() => reject.mutate()}
            >
              Reject
            </button>
          </div>
        </div>
      ) : null}
    </li>
  );
}

function TemplateCuration(): React.JSX.Element {
  const queryClient = useQueryClient();

  const templates = useQuery({ queryKey: ['templates'], queryFn: fetchTemplates });

  const refresh = async (): Promise<void> => {
    await queryClient.invalidateQueries({ queryKey: ['templates'] });
    await queryClient.invalidateQueries({ queryKey: ['site-audit'] });
  };

  const designate = useMutation({
    mutationFn: ({ id, kind }: { id: string; kind: DesignationKind }) =>
      designateTemplate(id, kind),
    onSuccess: refresh,
  });

  const withdraw = useMutation({
    mutationFn: ({ id, kind }: { id: string; kind: DesignationKind }) =>
      withdrawDesignation(id, kind),
    onSuccess: refresh,
  });

  const error = [templates.error, designate.error, withdraw.error].find(
    (e) => e instanceof Error,
  ) as ApiError | undefined;

  const busy = designate.isPending || withdraw.isPending;

  // Only what a curator could plausibly point somebody at. A private template is nobody's
  // authoritative starting point, and designating one would advertise something they cannot open.
  const candidates = (templates.data ?? []).filter(
    (t) => t.visibility === 'public' && t.retired_at === null,
  );

  return (
    <section className="org__card">
      <p className="org__eyebrow">Template curation</p>
      <p className="org__body org__body--strong">
        Designation changes discoverability. It never changes authority.
      </p>
      <p className="org__fine">
        Marking a template Official does not make it correct, does not make you its owner, and does
        not let anyone edit it who could not before. It says: this is the one to reach for.
      </p>

      {error ? <Notice referenceId={error.correlationId}>{error.message}</Notice> : null}

      {templates.isPending ? (
        <p className="org__fine">Loading…</p>
      ) : candidates.length === 0 ? (
        <p className="org__fine">
          No public templates yet. Only public ones can be designated — a private template is nobody
          else&rsquo;s starting point.
        </p>
      ) : (
        <ul className="org__list">
          {candidates.map((template) => (
            <li key={template.id} className="org__row">
              <div className="org__row-main">
                <div className="org__row-name">{template.name}</div>
                <div className="org__row-meta">
                  {template.latest_published_version === null
                    ? 'Nothing published'
                    : `v${template.latest_published_version}`}
                  {template.designations.length > 0 ? ` · ${template.designations.join(', ')}` : ''}
                </div>
              </div>

              <div className="org__designations">
                {(['official', 'featured'] as const).map((kind) => {
                  const on = template.designations.includes(kind);

                  return (
                    <button
                      key={kind}
                      type="button"
                      className={on ? 'org__btn org__btn--quiet' : 'org__btn'}
                      disabled={busy || template.latest_published_version === null}
                      title={DESIGNATION_COPY[kind].note}
                      onClick={() =>
                        on
                          ? withdraw.mutate({ id: template.id, kind })
                          : designate.mutate({ id: template.id, kind })
                      }
                    >
                      {on
                        ? `Withdraw ${DESIGNATION_COPY[kind].label}`
                        : DESIGNATION_COPY[kind].label}
                    </button>
                  );
                })}
              </div>
            </li>
          ))}
        </ul>
      )}

      <p className="org__fine">
        {/* Both directions are governance acts, and both are recorded. */}
        Designating and withdrawing are both recorded in the audit, naming you.
      </p>
    </section>
  );
}

function AuditLog(): React.JSX.Element {
  const { t } = useTranslation('common');
  const audit = useQuery({ queryKey: ['site-audit'], queryFn: () => fetchAudit() });

  return (
    <section className="org__card">
      <p className="org__eyebrow">Administrative audit</p>
      <p className="org__fine">
        Governance actions only. Not a log of what people planned, and not a technical log — entries
        here are written once and never change.
      </p>

      {audit.isPending ? (
        <p className="org__fine">Loading…</p>
      ) : audit.data?.data.length === 0 ? (
        <p className="org__fine">No governance actions have been taken yet.</p>
      ) : (
        <ul className="org__list">
          {(audit.data?.data ?? []).map((entry) => (
            <li key={entry.id} className="org__audit">
              <div className="org__audit-when">
                {entry.occurred_at.slice(0, 16).replace('T', ' ')}
              </div>
              <div className="org__audit-what">
                <strong>{entry.actor}</strong> {t(`audit_actions.${entry.action}`) ?? entry.action}
                {entry.resource_label ? ` — ${entry.resource_label}` : ''}
                {/* Shown, because an action taken for a reason should display the reason next to
                    it rather than hiding it behind a click. */}
                {entry.reason ? <div className="org__audit-reason">{entry.reason}</div> : null}
              </div>
              {entry.outcome === 'succeeded' ? (
                <span />
              ) : (
                <span className={`org-pill org-pill--${entry.outcome}`}>{entry.outcome}</span>
              )}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function OrganizationsManager(): React.JSX.Element {
  const queryClient = useQueryClient();
  const { account } = useAuth();
  const [search, setSearch] = useState('');
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState('');
  const [newKind, setNewKind] = useState<OrganizationKind>('parish');
  const [retiringId, setRetiringId] = useState<string | null>(null);
  const [assigningOrgId, setAssigningOrgId] = useState<string | null>(null);
  const [adminInput, setAdminInput] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [assignSuccess, setAssignSuccess] = useState<string | null>(null);

  const orgs = useQuery({
    queryKey: ['organizations-all'],
    queryFn: () => fetchOrganizations({}),
  });

  const accountSuggestions = useQuery({
    queryKey: ['site-accounts-search', adminInput],
    queryFn: () => fetchSiteAccounts(adminInput.trim()),
    enabled: Boolean(adminInput.trim().length >= 1),
  });

  const create = useMutation({
    mutationFn: () => createOrganizationDirectly({ name: newName.trim(), kind: newKind }),
    onSuccess: async () => {
      setCreating(false);
      setNewName('');
      await queryClient.invalidateQueries({ queryKey: ['organizations'] });
      await queryClient.invalidateQueries({ queryKey: ['organizations-all'] });
    },
  });

  const retire = useMutation({
    mutationFn: (id: string) => retireOrganization(id),
    onSuccess: async () => {
      setRetiringId(null);
      await queryClient.invalidateQueries({ queryKey: ['organizations'] });
      await queryClient.invalidateQueries({ queryKey: ['organizations-all'] });
    },
  });

  const assignAdmin = useMutation({
    mutationFn: ({ orgId, identifier }: { orgId: string; identifier: string }) =>
      assignSiteOrganizationAdmin(orgId, identifier),
    onSuccess: (data) => {
      setAssignSuccess(`Administrator assigned: ${data.display_name ?? data.account_id}`);
      setAdminInput('');
      setShowSuggestions(false);
      setTimeout(() => {
        setAssigningOrgId(null);
        setAssignSuccess(null);
      }, 3000);
      queryClient.invalidateQueries({ queryKey: ['organizations'] });
      queryClient.invalidateQueries({ queryKey: ['organizations-all'] });
    },
  });

  const filtered = (orgs.data?.data ?? []).filter(
    (o) =>
      o.name.toLowerCase().includes(search.toLowerCase()) ||
      o.kind.toLowerCase().includes(search.toLowerCase()),
  );

  return (
    <section className="org__card">
      <div className="org__card-head">
        <p className="org__eyebrow">All Organizations</p>
        <button
          type="button"
          className="org__btn org__btn--quiet"
          onClick={() => setCreating(!creating)}
        >
          {creating ? 'Cancel' : '+ New Organization'}
        </button>
      </div>

      {creating ? (
        <div
          style={{
            marginTop: 16,
            padding: '14px',
            background: 'var(--panel-warm)',
            border: '1px solid var(--gold-rule)',
          }}
        >
          <p className="org__eyebrow" style={{ margin: '0 0 8px' }}>
            Create Organization Directly
          </p>
          <label className="org__field">
            <span className="org__field-label">Name</span>
            <input
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder="e.g. Diocese of Springfield"
            />
          </label>
          <label className="org__field">
            <span className="org__field-label">Kind</span>
            <select
              value={newKind}
              onChange={(e) => setNewKind(e.target.value as OrganizationKind)}
            >
              {(Object.keys(ORGANIZATION_KIND_LABELS) as OrganizationKind[]).map((k) => (
                <option key={k} value={k}>
                  {ORGANIZATION_KIND_LABELS[k]}
                </option>
              ))}
            </select>
          </label>

          {create.error ? (
            <p className="org__fine" style={{ color: 'var(--red)' }}>
              {(create.error as ApiError).message}
            </p>
          ) : null}

          <div className="org__actions" style={{ marginTop: 12 }}>
            <button
              type="button"
              className="org__btn"
              disabled={create.isPending || !newName.trim()}
              onClick={() => create.mutate()}
            >
              {create.isPending ? 'Creating…' : 'Create Organization'}
            </button>
            <button type="button" className="org__ghost" onClick={() => setCreating(false)}>
              Cancel
            </button>
          </div>
        </div>
      ) : null}

      <div className="org__filters" style={{ marginTop: 14 }}>
        <input
          className="org__search"
          type="search"
          placeholder="Filter organizations…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      {orgs.isPending ? (
        <p className="org__fine">Loading organizations…</p>
      ) : filtered.length === 0 ? (
        <p className="org__fine" style={{ padding: '16px 0' }}>
          No organizations match.
        </p>
      ) : (
        <ul className="org__list">
          {filtered.map((org) => (
            <li key={org.id} className="org__row">
              <div className="org__row-main">
                <div className="org__row-name">
                  <strong>{org.name}</strong>
                  <span className="org__row-meta" style={{ marginLeft: 8 }}>
                    · {ORGANIZATION_KIND_LABELS[org.kind] ?? org.kind}
                  </span>
                </div>
                {org.retired_at ? (
                  <div className="org__row-meta" style={{ color: 'var(--red)' }}>
                    Retired {org.retired_at.slice(0, 10)}
                  </div>
                ) : null}

                {assigningOrgId === org.id ? (
                  <div
                    style={{
                      marginTop: 10,
                      padding: 10,
                      background: 'var(--panel-warm)',
                      border: '1px solid var(--gold-rule)',
                    }}
                  >
                    <p className="org__eyebrow" style={{ margin: '0 0 6px' }}>
                      Assign Administrator to {org.name}
                    </p>

                    {account ? (
                      <div style={{ marginBottom: 8 }}>
                        <button
                          type="button"
                          className="button button--ghost"
                          style={{ padding: '4px 8px', fontSize: 11 }}
                          onClick={() =>
                            assignAdmin.mutate({ orgId: org.id, identifier: account.id })
                          }
                        >
                          ⚡ Make myself administrator ({account.display_name})
                        </button>
                      </div>
                    ) : null}

                    <div style={{ position: 'relative' }}>
                      <div
                        style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}
                      >
                        <input
                          style={{ flex: '1 1 220px', padding: '6px 10px', fontSize: 13 }}
                          placeholder="Search by name, email, or Account ID"
                          value={adminInput}
                          onChange={(e) => {
                            setAdminInput(e.target.value);
                            setShowSuggestions(true);
                          }}
                          onFocus={() => setShowSuggestions(true)}
                        />
                        <button
                          type="button"
                          className="org__btn"
                          disabled={assignAdmin.isPending || !adminInput.trim()}
                          onClick={() =>
                            assignAdmin.mutate({ orgId: org.id, identifier: adminInput.trim() })
                          }
                        >
                          {assignAdmin.isPending ? 'Assigning…' : 'Appoint Admin'}
                        </button>
                        <button
                          type="button"
                          className="org__ghost"
                          onClick={() => {
                            setAssigningOrgId(null);
                            setAdminInput('');
                            setShowSuggestions(false);
                            setAssignSuccess(null);
                          }}
                        >
                          Cancel
                        </button>
                      </div>

                      {showSuggestions && (accountSuggestions.data?.data ?? []).length > 0 ? (
                        <div
                          style={{
                            position: 'absolute',
                            top: '100%',
                            left: 0,
                            right: 0,
                            zIndex: 30,
                            background: 'var(--panel)',
                            border: '1px solid var(--gold-rule)',
                            boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                            maxHeight: 180,
                            overflowY: 'auto',
                            marginTop: 4,
                          }}
                        >
                          {(accountSuggestions.data?.data ?? []).map((acc) => (
                            <button
                              key={acc.id}
                              type="button"
                              style={{
                                width: '100%',
                                padding: '6px 10px',
                                textAlign: 'left',
                                background: 'none',
                                border: 'none',
                                borderBottom: '1px solid var(--border-soft)',
                                cursor: 'pointer',
                              }}
                              onClick={() => {
                                setAdminInput(acc.email || acc.id);
                                setShowSuggestions(false);
                              }}
                            >
                              <div style={{ fontWeight: 600, fontSize: 12 }}>
                                {acc.display_name}
                              </div>
                              <div style={{ fontSize: 10.5, color: 'var(--muted-strong)' }}>
                                {acc.email} · ID: {acc.id}
                              </div>
                            </button>
                          ))}
                        </div>
                      ) : null}
                    </div>

                    {assignSuccess ? (
                      <p className="org__fine" style={{ color: 'var(--blue)', marginTop: 6 }}>
                        {assignSuccess}
                      </p>
                    ) : null}
                    {assignAdmin.error ? (
                      <p className="org__fine" style={{ color: 'var(--red)', marginTop: 6 }}>
                        {(assignAdmin.error as ApiError).message}
                      </p>
                    ) : null}
                  </div>
                ) : null}
              </div>

              <div className="org__row-acts">
                <button
                  type="button"
                  className="org__btn org__btn--quiet"
                  onClick={() => {
                    setAssigningOrgId(assigningOrgId === org.id ? null : org.id);
                    setAdminInput('');
                    setAssignSuccess(null);
                  }}
                >
                  + Admin
                </button>

                <Link
                  className="org__btn org__btn--quiet"
                  to="/organizations/$id/admin"
                  params={{ id: org.id }}
                >
                  Manage →
                </Link>

                {!org.retired_at ? (
                  retiringId === org.id ? (
                    <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                      <button
                        type="button"
                        className="org__btn"
                        style={{ background: 'var(--red)' }}
                        disabled={retire.isPending}
                        onClick={() => retire.mutate(org.id)}
                      >
                        Confirm retire
                      </button>
                      <button
                        type="button"
                        className="org__ghost"
                        onClick={() => setRetiringId(null)}
                      >
                        Cancel
                      </button>
                    </div>
                  ) : (
                    <button
                      type="button"
                      className="org__ghost"
                      style={{ color: 'var(--red)' }}
                      onClick={() => setRetiringId(org.id)}
                    >
                      Retire
                    </button>
                  )
                ) : null}
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function AccountsManager(): React.JSX.Element {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [disablingId, setDisablingId] = useState<string | null>(null);
  const [disableReason, setDisableReason] = useState('');

  const accounts = useQuery({
    queryKey: ['site-accounts', search],
    queryFn: () => fetchSiteAccounts(search),
  });

  const disable = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) => disableAccount(id, reason),
    onSuccess: async () => {
      setDisablingId(null);
      setDisableReason('');
      await queryClient.invalidateQueries({ queryKey: ['site-accounts'] });
    },
  });

  const enable = useMutation({
    mutationFn: (id: string) => enableAccount(id),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['site-accounts'] });
    },
  });

  return (
    <section className="org__card">
      <div className="org__card-head">
        <p className="org__eyebrow">Platform Accounts</p>
      </div>
      <p className="org__fine">
        Platform accounts and governance. Disabling an account preserves all their contributions and
        liturgies while preventing future sign-in.
      </p>

      <div className="org__filters" style={{ marginTop: 14 }}>
        <input
          className="org__search"
          type="search"
          placeholder="Search by name, email, or account ID…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      {accounts.isPending ? (
        <p className="org__fine">Loading accounts…</p>
      ) : accounts.error ? (
        <p className="org__fine" style={{ padding: '16px 0', color: 'var(--red)' }}>
          {(accounts.error as Error).message === 'resource_not_found'
            ? 'You do not have permission to view site accounts.'
            : (accounts.error as Error).message}
        </p>
      ) : (accounts.data?.data ?? []).length === 0 ? (
        <p className="org__fine" style={{ padding: '16px 0' }}>
          No accounts found.
        </p>
      ) : (
        <ul className="org__list">
          {(accounts.data?.data ?? []).map((account) => (
            <li key={account.id} className="org__row">
              <div className="org__row-main">
                <div className="org__row-name">
                  <strong>{account.display_name}</strong>
                  <span className="org__row-meta" style={{ marginLeft: 8 }}>
                    ({account.email})
                  </span>
                </div>
                <div
                  className="org__row-meta"
                  style={{ fontFamily: 'var(--font-mono)', fontSize: '10px' }}
                >
                  ID: {account.id} · Joined {account.created_at.slice(0, 10)}
                </div>
              </div>

              <div className="org__row-acts">
                <span
                  className={`org-pill ${account.status === 'active' ? 'org-pill--approved' : 'org-pill--rejected'}`}
                >
                  {account.status}
                </span>

                {account.status === 'active' ? (
                  disablingId === account.id ? (
                    <div
                      style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}
                    >
                      <input
                        style={{ padding: '4px 8px', fontSize: 12 }}
                        placeholder="Reason for disabling"
                        value={disableReason}
                        onChange={(e) => setDisableReason(e.target.value)}
                      />
                      <button
                        type="button"
                        className="org__btn"
                        style={{ background: 'var(--red)' }}
                        disabled={disable.isPending || !disableReason.trim()}
                        onClick={() =>
                          disable.mutate({ id: account.id, reason: disableReason.trim() })
                        }
                      >
                        Confirm Disable
                      </button>
                      <button
                        type="button"
                        className="org__ghost"
                        onClick={() => {
                          setDisablingId(null);
                          setDisableReason('');
                        }}
                      >
                        Cancel
                      </button>
                    </div>
                  ) : (
                    <button
                      type="button"
                      className="org__ghost"
                      style={{ color: 'var(--red)' }}
                      onClick={() => setDisablingId(account.id)}
                    >
                      Disable
                    </button>
                  )
                ) : (
                  <button
                    type="button"
                    className="org__btn org__btn--quiet"
                    disabled={enable.isPending}
                    onClick={() => enable.mutate(account.id)}
                  >
                    Enable
                  </button>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
